import { defineComponent as _defineComponent } from 'vue'
import { computed as _computed } from 'vue';

/**
 *
 * Displays a set of user and team avatars
 * Optional features to use facepile style (with icon only), truncate the list, and prioritize the current user
 *
 */
import { getModule } from 'vuex-module-decorators';
import type { UserDto } from '@/api';
import { NO_OWNER_ID } from '@/components/SAvatars/SAvatars';
import authModule from '@/store/modules/auth';
import store from '@/store';
import stringToInitials from '@/utils/filters/stringToInitials';
import initialsToColor from '@/utils/initialsToColor';
import i18n from '@/i18n';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';

type SAvatarsUser = UserDto & { type?: string };


interface SAvatarsProps {
  /**
   * List of users and teams to display
   */
  users: SAvatarsUser[];
  /**
   * Whether to limit displayed users to MAX_AVATARS
   */
  truncate?: boolean;
  /**
   * Whether to use facepile style (right-to-left order)
   */
  facepile?: boolean;
  /**
   * Whether to reduce the spacing between avatars
   */
  tighten?: boolean;
  /**
   * Whether to wrap the avatars in container, with line breaks if needed
   */
  wrapped?: boolean;
  /**
   * Size of the avatars
   */
  size?: number | string;
  /**
   * Whether to prioritize current user
   */
  prioritizeCurrentUser?: boolean;
}


const MAX_AVATARS = 5

export default /*#__PURE__*/_defineComponent({
  __name: 'SAvatars',
  props: {
  users: null,
  truncate: { type: Boolean, default: false },
  facepile: { type: Boolean, default: false },
  tighten: { type: Boolean, default: false },
  wrapped: { type: Boolean },
  size: { default: 22 },
  prioritizeCurrentUser: { type: Boolean, default: true }
},
  setup(__props) {

const props = __props;
const auth = getModule(authModule, store);



// Whether the list is truncated
const isTruncated = _computed(() => props.truncate && props.users.length > MAX_AVATARS);

/**
 * Utility functions for user display
 */
const isTeam = (user: SAvatarsUser): boolean => user.type === 'team' || user.type === 'TEAM';

// List of users to be displayed, with optional reordering and truncation
const visibleUsers = _computed(() => {
  const tempUsers = [...props.users];

  // Optional: Move current user to the front (only for users, not teams)
  if (props.prioritizeCurrentUser) {
    const currentUserId = auth.getUser ? auth.getUser.id : null;
    const currentUserIndex = currentUserId != null ? tempUsers.findIndex((user) => !isTeam(user) && user.id === currentUserId) : -1;

    if (currentUserIndex !== -1) {
      const currentUser = tempUsers.splice(currentUserIndex, 1);
      tempUsers.unshift(currentUser[0]);
    }
  }
  const usersToDisplay = props.truncate ? tempUsers.slice(0, MAX_AVATARS) : tempUsers;
  return props.facepile ? usersToDisplay.reverse() : usersToDisplay;
});

// Text to display when users are truncated
const plusUsersText = _computed(() => `+${props.users.length - MAX_AVATARS}`);

const isNoOwnerId = (user: UserDto) => user.id === NO_OWNER_ID;

/**
 * Format name special case for current user
 * @param user
 */
const getUserName = (user: SAvatarsUser) => {
  if (!user || !user.name) return '';
  if (isTeam(user)) return user.name;
  const isCurrentUser = auth.getUser && (auth.getUser.id === user.id || auth.getUser.email === user.id);
  return isCurrentUser ? i18n.t('users.current_user_display', { user: user.name }) : user.name;
};

/**
 * Get user initials
 * @param user
 */
const getUserInitials = (user: UserDto) => (user?.name ? stringToInitials(user.name) : '');

const getTeamInitials = (team: SAvatarsUser): string => (team?.name ? stringToInitials(team.name) : '');

/**
 * Get user color based on initials
 * @param user
 */
const getUserColor = (user: UserDto) => (user?.name ? initialsToColor(user.name) : '');

return { __sfc: true,MAX_AVATARS, auth, props, isTruncated, isTeam, visibleUsers, plusUsersText, isNoOwnerId, getUserName, getUserInitials, getTeamInitials, getUserColor, _computed, getModule, NO_OWNER_ID, authModule, store, stringToInitials, initialsToColor, i18n, SEllipsisTooltip }
}

})